"use client"

import { useCallback } from "react"
import { useSearchParams } from "next/navigation"
import { LexicalText, Mention } from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { useNoteDialogStore } from "@/stores/use-note-dialog-store"
import { useQueryClient } from "@tanstack/react-query"

import { useLoadingRouter } from "@/hooks/use-loading-router"

import { NoteDialog } from "./note-dialog"
import { shouldWriteNoteDialogQuery } from "./note-dialog-url"

const createDialogId = (noteId: string, householdId?: string, contactId?: string, businessId?: string) => {
  if (noteId) return noteId
  const scope = householdId ?? contactId ?? businessId ?? "note"
  return `note-draft-${scope}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

interface OpenNoteDialogOptions {
  noteId: string
  householdId?: string
  contactId?: string
  businessId?: string
  prefillContent?: LexicalText
  prefillMentions?: Mention[]
  onSaved?: () => void
}

export function useNoteDialog() {
  const { openDialog, closeDialog } = useGlobalDialogStore()
  const searchParams = useSearchParams()
  const { replace } = useLoadingRouter()
  const queryClient = useQueryClient()
  const initializeDraft = useNoteDialogStore((state) => state.initializeDraft)
  const updateDraft = useNoteDialogStore((state) => state.updateDraft)

  const open = useCallback(
    ({
      noteId,
      householdId,
      contactId,
      businessId,
      prefillContent,
      prefillMentions,
      onSaved,
    }: OpenNoteDialogOptions) => {
      if (noteId) {
        queryClient.removeQueries({ queryKey: ["note", noteId] })
      }

      const dialogId = createDialogId(noteId, householdId, contactId, businessId)
      const timestamp = Date.now()

      initializeDraft(dialogId, {
        noteId,
        selectedHouseholdId: householdId ?? null,
        selectedContactId: contactId ?? null,
        selectedBusinessId: businessId ?? null,
        ...((prefillContent || (prefillMentions && prefillMentions.length > 0)) && {
          note: {
            ...(prefillContent ? { content: prefillContent } : {}),
            ...(prefillMentions && prefillMentions.length > 0 ? { mention_target_labels: prefillMentions } : {}),
          },
        }),
        ...(onSaved ? { onSaved } : {}),
      })

      if (
        prefillContent ||
        (prefillMentions && prefillMentions.length > 0) ||
        onSaved ||
        householdId ||
        contactId ||
        businessId
      ) {
        updateDraft(dialogId, (current) => ({
          ...current,
          selectedHouseholdId: householdId ?? current.selectedHouseholdId ?? null,
          selectedContactId: contactId ?? current.selectedContactId ?? null,
          selectedBusinessId: businessId ?? current.selectedBusinessId ?? null,
          note:
            prefillContent || (prefillMentions && prefillMentions.length > 0)
              ? {
                  ...current.note,
                  ...(prefillContent ? { content: prefillContent } : {}),
                  ...(prefillMentions && prefillMentions.length > 0 ? { mention_target_labels: prefillMentions } : {}),
                }
              : current.note,
          onSaved: onSaved ?? current.onSaved,
        }))
      }

      const closeCallback = () => {
        const params = new URLSearchParams(searchParams.toString())
        params.delete("dialog")
        params.delete("id")
        replace(`?${params.toString()}`)

        if (noteId) {
          queryClient.removeQueries({ queryKey: ["note", noteId] })
        }
      }

      openDialog({
        variant: "custom",
        content: (
          <NoteDialog
            key={`note-${dialogId}-${timestamp}`}
            noteId={noteId}
            dialogId={dialogId}
            householdId={householdId}
            contactId={contactId}
            businessId={businessId}
            initialContent={prefillContent}
            initialMentions={prefillMentions}
            onSaved={onSaved}
          />
        ),
        dialogContentClasses: "h-[min(48rem,calc(100vh-8rem))] max-h-[calc(100vh-8rem)] max-w-2xl overflow-hidden",
        closeCallback,
      })

      if (shouldWriteNoteDialogQuery(searchParams, noteId)) {
        const params = new URLSearchParams(searchParams.toString())
        params.set("dialog", "note")
        params.set("id", noteId)
        replace(`?${params.toString()}`)
      }
    },
    [openDialog, queryClient, replace, searchParams, initializeDraft, updateDraft]
  )

  const close = useCallback(() => {
    closeDialog()
  }, [closeDialog])

  return { open, close }
}
